import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { TableColumnConfig, TableRowConfig } from '@repo/sdk/models/components';

import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';

import {
  createSnapshot,
  getColumnUnitInfo,
  getDisabledColumns,
} from '../equipment-bulk-edit.helpers';
import type { EquipmentBulkEditFormValues } from '../equipment-bulk-edit.types';

interface UseEquipmentBulkEditPushoutStateOptions {
  columns: TableColumnConfig[];
  rows: TableRowConfig[];
  selectedRowIds: string[];
  onClose: () => void;
}

interface PushoutDiscardDialog {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

interface UseEquipmentBulkEditPushoutStateReturn {
  selectedColumnIds: string[];
  setSelectedColumnIds: (ids: string[]) => void;
  selectedColumns: TableColumnConfig[];
  bulkValues: Record<string, string>;
  hasChanges: boolean;
  handleBulkValueChange: (columnId: string, value: string) => void;
  dirtyColumnIds: Set<string>;
  disabledColumnIds: Set<string>;
  disabledColumnReasons: Map<string, string>;
  handleApply: () => void;
  handleCloseAttempt: () => void;
  handleDiscardAttempt: () => void;
  handleDiscardColumns: (columnIds: string[]) => void;
  discardDialog: PushoutDiscardDialog;
}

/**
 * useEquipmentBulkEditPushoutState
 *
 * Encapsulates all state management for the bulk edit pushout:
 * bulk values, form synchronization via React Hook Form, snapshot-based
 * discard/revert, dirty tracking, and close-with-guard logic.
 */
export const useEquipmentBulkEditPushoutState = ({
  columns,
  rows,
  selectedRowIds,
  onClose,
}: UseEquipmentBulkEditPushoutStateOptions): UseEquipmentBulkEditPushoutStateReturn => {
  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([]);
  const [bulkValues, setBulkValues] = useState<Record<string, string>>({});
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const {
    getValues,
    setValue,
    formState: { dirtyFields },
  } = useFormContext<{ values: EquipmentBulkEditFormValues }>();

  // Capture snapshot once on mount (when drawer opens)
  const [snapshot] = useState(() => {
    const allValues = getValues('values');
    return createSnapshot(allValues, selectedRowIds);
  });

  // Change detection - Apply enabled when any bulk value is non-empty
  const hasChanges = Object.values(bulkValues).some((v) => v !== '');

  // Derive which columns have dirty fields across the selected rows
  const dirtyColumnIds = useMemo(() => {
    const ids = new Set<string>();
    const dirtyValues = dirtyFields.values;
    if (!dirtyValues) return ids;
    for (const rowId of selectedRowIds) {
      const rowDirty = dirtyValues[rowId];
      if (rowDirty) {
        for (const columnId of Object.keys(rowDirty)) {
          if (rowDirty[columnId]) ids.add(columnId);
        }
      }
    }
    return ids;
  }, [dirtyFields, selectedRowIds]);

  const { ids: disabledColumnIds, reasons: disabledColumnReasons } = useMemo(
    () => getDisabledColumns(columns, rows, selectedRowIds),
    [columns, rows, selectedRowIds]
  );

  const selectedColumns = useMemo(() => {
    return columns.filter((column) => {
      // Filter out columns that are not selected.
      if (!selectedColumnIds.includes(column.columnId)) return false;
      // Filter out columns that are not supported by the rows to edit.
      return rows.some((row) => row.refs?.some((ref) => ref.columnId === column.columnId));
    });
  }, [columns, rows, selectedColumnIds]);

  const handleBulkValueChange = (columnId: string, value: string) => {
    setBulkValues((prev) => ({ ...prev, [columnId]: value }));

    // Derive unit info from row refs (not form values, which are plain strings)
    const unitInfo = getColumnUnitInfo(columnId, rows, selectedRowIds);

    // Convert display value to source value for form storage
    const sourceValue = unitInfo
      ? formatUnits({ value, units: unitInfo.displayUnits }, unitInfo.sourceUnits).valueLabel
      : value;

    // Apply to each selected row that has this column, using setValue
    // so that each field is properly marked as dirty in React Hook Form.
    const currentValues = getValues('values');
    for (const rowId of selectedRowIds) {
      if (currentValues[rowId] && columnId in currentValues[rowId]) {
        setValue(`values.${rowId}.${columnId}`, sourceValue, { shouldDirty: true });
      }
    }
  };

  const handleDiscard = () => {
    // Restore snapshot values using setValue to maintain proper value tracking.
    for (const rowId of selectedRowIds) {
      if (snapshot[rowId]) {
        for (const [columnId, value] of Object.entries(snapshot[rowId])) {
          setValue(`values.${rowId}.${columnId}`, value, {
            shouldDirty: true,
            shouldTouch: false,
          });
        }
      }
    }

    // Clear bulk values
    setBulkValues({});
    onClose();
  };

  const handleApply = () => {
    // Clear bulk values (changes already applied via handleBulkValueChange)
    setBulkValues({});
    onClose();
  };

  const handleDiscardColumns = (columnIds: string[]) => {
    // Revert form values for discarded columns across all selected rows
    for (const rowId of selectedRowIds) {
      for (const columnId of columnIds) {
        const originalValue = snapshot[rowId]?.[columnId];
        if (originalValue !== undefined) {
          setValue(`values.${rowId}.${columnId}`, originalValue, {
            shouldDirty: true,
            shouldTouch: false,
          });
        }
      }
    }
    // Clear any bulk values for discarded columns
    setBulkValues((prev) => {
      const next = { ...prev };
      for (const columnId of columnIds) {
        delete next[columnId];
      }
      return next;
    });
  };

  const handleCloseAttempt = () => {
    if (hasChanges) {
      setDiscardDialogOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardAttempt = () => {
    if (hasChanges) {
      setDiscardDialogOpen(true);
    } else {
      handleDiscard();
    }
  };

  return {
    selectedColumnIds,
    setSelectedColumnIds,
    selectedColumns,
    bulkValues,
    hasChanges,
    handleBulkValueChange,
    dirtyColumnIds,
    disabledColumnIds,
    disabledColumnReasons,
    handleApply,
    handleCloseAttempt,
    handleDiscardAttempt,
    handleDiscardColumns,
    discardDialog: {
      open: discardDialogOpen,
      onOpenChange: setDiscardDialogOpen,
      onConfirm: handleDiscard,
    },
  };
};
